<template>
  <v-app style="background-color: #f8f9fb">
    <v-overlay :value="loading" z-index="9999">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>

    <v-container>
      <!-- Aviso com a ordem de uso da tela -->
      <v-row class="mb-0">
        <v-col cols="12" class="pb-1">
          <v-alert
            type="info"
            text
            dense
            icon="mdi-information-outline"
            class="mb-2"
          >
            <div class="d-flex align-center flex-wrap" style="font-size: 13px;">
              <span class="font-weight-bold mr-2">Ordem de uso:</span>
              <span><strong>1º</strong> Selecione a Empresa</span>
              <v-icon small color="info" class="mx-1">mdi-arrow-right</v-icon>
              <span><strong>2º</strong> Selecione o Departamento</span>
              <v-icon small color="info" class="mx-1">mdi-arrow-right</v-icon>
              <span><strong>3º</strong> Filtros Opcionais (se desejar)</span>
              <v-icon small color="info" class="mx-1">mdi-arrow-right</v-icon>
              <span><strong>4º</strong> Clique em <strong>Filtrar</strong></span>
            </div>
          </v-alert>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" class="pt-0">
          <FiltroDinamicoGLPI @filtrar="onFiltrar" />
        </v-col>
      </v-row>

      <v-row v-if="!dadosCarregados" justify="center" class="mt-8">
        <v-col cols="12" class="text-center">
          <v-icon size="56" color="grey lighten-1">mdi-filter-outline</v-icon>
          <h3 class="grey--text text--darken-1 font-weight-regular mt-2">
            Selecione a <strong>Empresa</strong> e o <strong>Departamento</strong> no filtro acima e clique em <strong>Filtrar</strong> para começar.
          </h3>
        </v-col>
      </v-row>

      <div v-else>
        <!-- Chave de Alternância: Solicitante vs Responsável e Período Ativo -->
        <v-row class="mt-2 mb-1" align="center" justify="space-between">
          <v-col cols="12" sm="auto" class="d-flex align-center flex-wrap">
            <span class="text-subtitle-2 font-weight-bold mr-3 grey--text text--darken-2">Visão por:</span>
            <v-btn-toggle v-model="tipoVisao" mandatory dense color="primary" @change="onTipoVisaoChange">
              <v-btn value="solicitante" small class="text-capitalize">
                <v-icon left small>mdi-account-arrow-right</v-icon> Solicitante (Quem abriu)
              </v-btn>
              <v-btn value="responsavel" small class="text-capitalize">
                <v-icon left small>mdi-account-wrench</v-icon> Responsável (Técnico)
              </v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="12" sm="auto" v-if="periodoFormatado" class="d-flex align-center">
            <v-chip small color="primary" outlined class="font-weight-medium">
              <v-icon left small>mdi-calendar-range</v-icon>
              {{ periodoFormatado }}
            </v-chip>
          </v-col>
        </v-row>

        <v-row class="mt-1">
          <v-col cols="12" sm="6" md="2">
            <base-material-card color="info" icon="mdi-account-group" class="px-4 py-3">
              <div class="text-subtitle-2 grey--text">
                {{ tipoVisao === 'solicitante' ? 'Solicitantes' : 'Responsáveis' }}
              </div>
              <h3 class="font-weight-bold">{{ tabelaDados.length }}</h3>
            </base-material-card>
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <base-material-card color="warning" icon="mdi-ticket-account" class="px-4 py-3">
              <div class="text-subtitle-2 grey--text">Totais</div>
              <h3 class="font-weight-bold">{{ totalChamadosSetor }}</h3>
            </base-material-card>
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <base-material-card color="error" icon="mdi-check-all" class="px-4 py-3">
              <div class="text-subtitle-2 grey--text">Resolvidos</div>
              <h3 class="font-weight-bold">{{ totalResolvidosSetor }}</h3>
            </base-material-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <base-material-card color="purple" icon="mdi-chart-pie" class="px-4 py-3">
              <div class="text-subtitle-2 grey--text">Taxa Conclusão</div>
              <h3 class="font-weight-bold">{{ taxaConclusaoSetor }}%</h3>
            </base-material-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <base-material-card color="success" icon="mdi-currency-usd" class="px-4 py-3">
              <div class="d-flex flex-column">
                <span class="text-subtitle-2 grey--text">Custo Total</span>
                <span class="red--text font-weight-bold" style="font-size: 11px;">(ainda não implementado)</span>
              </div>
              <h3 class="font-weight-bold mt-1">R$ {{ totalCustoSetor }}</h3>
            </base-material-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <base-material-card icon="mdi-table-account" :title="tipoVisao === 'solicitante' ? 'Detalhamento por Solicitante' : 'Detalhamento por Responsável'" color="blue"
              class="px-5 py-3">
              <div class="caption grey--text mb-3 d-flex align-center">
                <v-icon small color="blue" class="mr-1">mdi-information-outline</v-icon>
                <span>O tempo médio calcula o período entre abertura e resolução (ou tempo decorrido em aberto para chamados não finalizados).</span>
              </div>
              <v-text-field v-model="search" append-icon="mdi-magnify" label="Pesquisar colaborador..." single-line
                hide-details class="mb-4"></v-text-field>
              <v-data-table :headers="dynamicHeaders" :items="tabelaDados" :search="search" :loading="loading"
                sort-by="qtdSolicitados" sort-desc class="elevation-0">
                <template v-slot:item.custoTotal="{ item }">
                  <span :class="item.custoTotal > 0 ? 'red--text font-weight-bold' : 'grey--text'">
                    {{ formatCurrency(item.custoTotal) }}
                  </span>
                </template>
                <template v-slot:item.mediaTempo="{ item }">
                  <v-chip small outlined color="blue-grey" class="font-weight-medium">
                    <v-icon left small color="primary">mdi-timer-outline</v-icon>
                    {{ item.mediaTempo || '00:00:00' }}
                  </v-chip>
                </template>
                <template v-slot:item.taxaResolucao="{ item }">
                  <v-progress-linear :value="item.qtdSolicitados > 0 ? (item.qtdResolvidos / item.qtdSolicitados) * 100 : 0" height="15" color="blue"
                    rounded>
                    <template v-slot:default="{ value }">
                      <strong style="color: white; font-size: 10px">{{ Math.ceil(value) }}%</strong>
                    </template>
                  </v-progress-linear>
                </template>
              </v-data-table>
            </base-material-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="4">
            <base-material-card :title="tipoVisao === 'solicitante' ? 'Top 5 Solicitantes' : 'Top 5 Responsáveis'" color="orange" class="px-5 py-3">
              <v-card-text>
                <apexchart type="bar" height="300" :options="chartOptionsTop5" :series="seriesTop5"></apexchart>
              </v-card-text>
            </base-material-card>
          </v-col>
          <v-col cols="12" md="8">
            <base-material-card title="Ranking de Solicitações por Empresa/Localização" color="purple"
              class="px-5 py-3">
              <v-card-text>
                <apexchart type="bar" height="300" :options="chartOptionsRanking" :series="seriesRanking"></apexchart>
              </v-card-text>
            </base-material-card>
          </v-col>
        </v-row>

        <!-- Listagem Detalhada de Chamados -->
        <v-row class="mt-4">
          <v-col cols="12">
            <base-material-card
              icon="mdi-ticket-confirmation-outline"
              title="Listagem de Chamados"
              color="indigo"
              class="px-5 py-3"
            >
              <div class="caption grey--text mb-3 d-flex align-center">
                <v-icon small color="indigo" class="mr-1">mdi-information-outline</v-icon>
                <span>Exibindo chamados filtrados de acordo com os parâmetros selecionados.</span>
              </div>
              <div class="d-flex align-center justify-space-between flex-wrap mb-4">
                <v-text-field
                  v-model="searchChamados"
                  append-icon="mdi-magnify"
                  label="Pesquisar chamados por ID, título, solicitante, técnico, localização..."
                  single-line
                  hide-details
                  style="max-width: 500px;"
                  class="mr-4 my-1"
                ></v-text-field>

                <v-btn
                  color="success"
                  class="white--text my-1"
                  small
                  :disabled="!tabelaChamados || tabelaChamados.length === 0"
                  :loading="exportandoExcel"
                  @click="exportarChamadosExcel"
                >
                  <v-icon left small>mdi-file-excel</v-icon>
                  Exportar Lista (Excel)
                </v-btn>
              </div>

              <v-data-table
                :headers="headersChamados"
                :items="tabelaChamados"
                :search="searchChamados"
                :loading="loading"
                :footer-props="{
                  'items-per-page-options': [10, 25, 50, 100],
                  'items-per-page-text': 'Chamados por página:'
                }"
                sort-by="idChamado"
                sort-desc
                class="elevation-0"
              >
                <template v-slot:item.idChamado="{ item }">
                  <span class="font-weight-bold grey--text text--darken-3">#{{ item.idChamado }}</span>
                </template>

                <template v-slot:item.solicitante="{ item }">
                  <span>{{ item.solicitante || 'Não informado' }}</span>
                </template>

                <template v-slot:item.tecnicoAtribuido="{ item }">
                  <span :class="item.tecnicoAtribuido ? '' : 'grey--text font-italic'">
                    {{ item.tecnicoAtribuido || 'Não atribuído' }}
                  </span>
                </template>

                <template v-slot:item.status="{ item }">
                  <v-chip
                    small
                    :color="getStatusColor(item.status)"
                    text-color="white"
                    class="font-weight-medium"
                  >
                    {{ item.status }}
                  </v-chip>
                </template>

                <template v-slot:item.localizacao="{ item }">
                  <span>{{ item.localizacao || 'Sem localização' }}</span>
                </template>

                <template v-slot:item.entidade="{ item }">
                  <span>{{ formatarEntidade(item.entidade) }}</span>
                </template>

                <template v-slot:item.acoes="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="primary"
                        :href="`https://chamados.grupoparque.com.br/front/ticket.form.php?id=${item.idChamado}`"
                        target="_blank"
                        rel="noopener noreferrer"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon small>mdi-open-in-new</v-icon>
                      </v-btn>
                    </template>
                    <span>Abrir chamado no GLPI (#{{ item.idChamado }})</span>
                  </v-tooltip>
                </template>
              </v-data-table>
            </base-material-card>
          </v-col>
        </v-row>

      </div>
    </v-container>
  </v-app>
</template>

<script>
import FiltroDinamicoGLPI from '@/components/base/FiltroDinamicoGLPI'
import Glpi from '@/services/glpi'
import VueApexCharts from 'vue-apexcharts'
import * as XLSX from 'xlsx'

export default {
  name: 'AcompanhamentoSetor',
  components: {
    FiltroDinamicoGLPI,
    apexchart: VueApexCharts
  },
  data() {
    return {
      loading: false,
      exportandoExcel: false,
      dadosCarregados: false,
      search: '',
      searchChamados: '',
      tipoVisao: 'solicitante', // 'solicitante' ou 'responsavel'
      filtrosAtuais: null,
      periodoFormatado: '',
      tabelaDados: [],
      rankingLocalizacaoDados: [],
      tabelaChamados: [],
      headersChamados: [
        { text: 'ID', value: 'idChamado', align: 'center', width: '90px' },
        { text: 'Título', value: 'tituloChamado', align: 'start' },
        { text: 'Solicitante', value: 'solicitante', align: 'start' },
        { text: 'Técnico Atribuído', value: 'tecnicoAtribuido', align: 'start' },
        { text: 'Status', value: 'status', align: 'center' },
        { text: 'Localização', value: 'localizacao', align: 'start' },
        { text: 'Entidade', value: 'entidade', align: 'start' },
        { text: 'Ação', value: 'acoes', align: 'center', sortable: false, width: '80px' }
      ],
      seriesTop5: [{ name: 'Chamados', data: [] }],
      chartOptionsTop5: {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 4, horizontal: false } },
        dataLabels: { enabled: true },
        xaxis: { categories: [] },
        colors: ['#fb8c00']
      },
      seriesRanking: [{ name: 'Volume', data: [] }],
      chartOptionsRanking: {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 4, horizontal: false } },
        dataLabels: { enabled: true },
        xaxis: { categories: [] },
        colors: ['#8e24aa']
      }
    }
  },
  computed: {
    dynamicHeaders() {
      const colLabel = this.tipoVisao === 'solicitante' ? 'Solicitante' : 'Responsável (Técnico)';
      return [
        { text: colLabel, align: 'start', value: 'nomeColaborador' },
        { text: 'Solicitados', value: 'qtdSolicitados', align: 'center' },
        { text: 'Resolvidos', value: 'qtdResolvidos', align: 'center' },
        { text: 'Taxa Resolução', value: 'taxaResolucao', align: 'center', sortable: false },
        { text: 'Média Tempo', value: 'mediaTempo', align: 'center' },
        { text: 'Custo Total', value: 'custoTotal', align: 'end' },
      ];
    },
    totalChamadosSetor() {
      return this.tabelaDados.reduce((acc, item) => acc + item.qtdSolicitados, 0);
    },
    totalResolvidosSetor() {
      return this.tabelaDados.reduce((acc, item) => acc + item.qtdResolvidos, 0);
    },
    taxaConclusaoSetor() {
      if (this.totalChamadosSetor === 0) return 0;
      return Math.ceil((this.totalResolvidosSetor / this.totalChamadosSetor) * 100);
    },
    totalCustoSetor() {
      const total = this.tabelaDados.reduce((acc, item) => acc + (item.custoTotal || 0), 0);
      return total.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
    },
    chamadosFiltradosPorBusca() {
      if (!this.searchChamados) return this.tabelaChamados;
      const term = this.searchChamados.toLowerCase().trim();
      return (this.tabelaChamados || []).filter(item => {
        return (
          String(item.idChamado || "").toLowerCase().includes(term) ||
          String(item.tituloChamado || "").toLowerCase().includes(term) ||
          String(item.solicitante || "").toLowerCase().includes(term) ||
          String(item.tecnicoAtribuido || "").toLowerCase().includes(term) ||
          String(item.status || "").toLowerCase().includes(term) ||
          String(item.localizacao || "").toLowerCase().includes(term) ||
          String(item.entidade || "").toLowerCase().includes(term)
        );
      });
    }
  },
  methods: {
    getStatusColor(status) {
      if (!status) return 'grey';
      const s = status.toLowerCase();
      if (s.includes('novo')) return 'info';
      if (s.includes('atendimento') || s.includes('atribuído') || s.includes('atribuido')) return 'primary';
      if (s.includes('planejado')) return 'purple';
      if (s.includes('pendente')) return 'warning';
      if (s.includes('resolvido') || s.includes('solucionado')) return 'success';
      if (s.includes('fechado')) return 'blue-grey';
      return 'grey';
    },

    formatCurrency(value) {
      if (!value) return 'R$ 0,00';
      return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },

    onTipoVisaoChange() {
      if (this.filtrosAtuais) {
        this.onFiltrar(this.filtrosAtuais);
      }
    },

    async onFiltrar(filtros) {
        if (!filtros.entityId) {
            alert("Por favor, selecione um Departamento no filtro.");
            return;
        }
        
        this.filtrosAtuais = { ...filtros };
        this.loading = true;
        
        try {
            const formatISO = (d) => {
              const year = d.getFullYear();
              const month = String(d.getMonth() + 1).padStart(2, '0');
              const day = String(d.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            };

            const formatBR = (iso) => {
              if (!iso) return '';
              const [y, m, d] = iso.split('-');
              return `${d}/${m}/${y}`;
            };

            const hoje = new Date();
            const dataFimPadrao = formatISO(hoje);
            const trintaDiasAtras = new Date(hoje);
            trintaDiasAtras.setDate(hoje.getDate() - 30);
            const dataInicioPadrao = formatISO(trintaDiasAtras);

            const dataInicioFinal = filtros.periodoInicial || dataInicioPadrao;
            const dataFimFinal = filtros.periodoFinal || dataFimPadrao;

            const pacoteFiltros = {
                entityId: filtros.entityId,
                tipoVisao: this.tipoVisao,
                dataInicio: dataInicioFinal,
                dataFim: dataFimFinal
            };
            
            if (filtros.locationId && filtros.locationId !== 'coorporativo') {
                pacoteFiltros.locationId = filtros.locationId;
            } else if (filtros.locationIds && filtros.locationIds.length > 0) {
                pacoteFiltros.locationIds = Array.isArray(filtros.locationIds) ? filtros.locationIds.join(',') : filtros.locationIds;
            }

            if (filtros.solicitanteIds && filtros.solicitanteIds.length > 0) {
                pacoteFiltros.solicitanteIds = Array.isArray(filtros.solicitanteIds) ? filtros.solicitanteIds.join(',') : filtros.solicitanteIds;
            } else if (filtros.userIds && filtros.userIds.length > 0) {
                pacoteFiltros.solicitanteIds = Array.isArray(filtros.userIds) ? filtros.userIds.join(',') : filtros.userIds;
            } else if (filtros.solicitanteId) {
                pacoteFiltros.solicitanteId = filtros.solicitanteId;
            } else if (filtros.userId) {
                pacoteFiltros.solicitanteId = filtros.userId;
            }

            if (filtros.responsavelId) pacoteFiltros.responsavelId = filtros.responsavelId;

            if (filtros.categoriaIds && filtros.categoriaIds.length > 0) {
                pacoteFiltros.categoriaIds = Array.isArray(filtros.categoriaIds) ? filtros.categoriaIds.join(',') : filtros.categoriaIds;
            } else if (filtros.categoriaId) {
                pacoteFiltros.categoriaId = filtros.categoriaId;
            }

            if (filtros.statusList && filtros.statusList.length > 0) {
                pacoteFiltros.statusList = Array.isArray(filtros.statusList) ? filtros.statusList.join(',') : filtros.statusList;
            } else if (Array.isArray(filtros.status) && filtros.status.length > 0) {
                pacoteFiltros.statusList = filtros.status.join(',');
            } else if (filtros.status) {
                pacoteFiltros.status = filtros.status;
            }
            
            this.periodoFormatado = `${formatBR(dataInicioFinal)} até ${formatBR(dataFimFinal)}${!filtros.periodoInicial ? ' (últimos 30 dias)' : ''}`;

            const [resPerformance, resRanking, resChamados] = await Promise.all([
                Glpi.performance_setor(pacoteFiltros),
                Glpi.ranking_localizacao(pacoteFiltros),
                Glpi.listar_chamados(pacoteFiltros)
            ]);
            
            this.tabelaDados = resPerformance.data || [];
            this.rankingLocalizacaoDados = resRanking.data || [];
            this.tabelaChamados = resChamados.data || [];
            this.dadosCarregados = true;
            this.atualizarGraficos();

        } catch (error) {
            console.error(error);
        } finally {
            this.loading = false;
        }
    },

    normalizarTexto(str) {
      return (str || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase()
        .trim();
    },

    agruparLocalizacoesPorEmpresa(dados) {
      const mapearEmpresa = (nomeLoc) => {
        const nomeNorm = this.normalizarTexto(nomeLoc);
        if (!nomeNorm || nomeNorm === "SEM LOCALIZACAO") {
          return "Sem Localização";
        }

        // 1. CEMITÉRIOS
        if (
          nomeNorm.includes("CEMITERIO PREVIDA SMC") ||
          nomeNorm.includes("CEMITERIO PREVIDA SAO MIGUEL") ||
          (nomeNorm.includes("CEMITERIO") && nomeNorm.includes("SMC"))
        ) {
          return "Cemiterio Previda SMC";
        }
        if (
          nomeNorm.includes("CEMITERIO PREVIDA ARA") ||
          nomeNorm.includes("CEMITERIO PREVIDA ARAPIRACA") ||
          (nomeNorm.includes("CEMITERIO") && !nomeNorm.includes("SMC") && !nomeNorm.includes("SAO MIGUEL"))
        ) {
          return "Cemiterio Previda ARA";
        }

        // 2. PREVIDA (12 filiais consolidadas como Previda)
        if (
          nomeNorm.startsWith("PREVIDA") ||
          nomeNorm.includes("VELORIOS") ||
          nomeNorm.includes("FUNERARIA PREVIDA") ||
          nomeNorm.includes("PREVIPARQ") ||
          nomeNorm.includes("SEDE PREVIDA") ||
          nomeNorm.includes("LOJA ARAPIRACA") ||
          nomeNorm.includes("LOJA CENTRO") ||
          nomeNorm.includes("LOJA FAROL") ||
          nomeNorm.includes("LOJA JACINTINHO") ||
          nomeNorm.includes("LOJA PALMEIRA") ||
          nomeNorm.includes("LOJA TRAPICHE") ||
          nomeNorm.includes("PATIO")
        ) {
          return "Previda";
        }

        // 3. PARQUE DAS FLORES
        if (nomeNorm.includes("PARQUE DAS FLORES") || nomeNorm.includes("FLORES DO PARQUE")) {
          return "Parque das Flores";
        }

        // 4. PARQUE DO AGRESTE
        if (nomeNorm.includes("PARQUE DO AGRESTE") || nomeNorm.includes("AGRESTE")) {
          return "Parque do Agreste";
        }

        // 5. MEMORIAL
        if (nomeNorm.includes("MEMORIAL")) {
          return "Memorial Parque de Maceió";
        }

        // 6. A FLOR DO CAFÉ
        if (nomeNorm.includes("FLOR DO CAFE") || nomeNorm.includes("FLOR DO CAFÉ") || nomeNorm.includes("CAFÉ")) {
          return "A flor do café";
        }

        return nomeLoc;
      };

      const agrupado = {};
      (dados || []).forEach(item => {
        const empresaNome = mapearEmpresa(item.nomeLocalizacao);
        agrupado[empresaNome] = (agrupado[empresaNome] || 0) + (item.qtdSolicitados || 0);
      });

      return Object.keys(agrupado)
        .map(nome => ({
          nomeLocalizacao: nome,
          qtdSolicitados: agrupado[nome]
        }))
        .sort((a, b) => b.qtdSolicitados - a.qtdSolicitados);
    },

    atualizarGraficos() {
      const top5 = [...this.tabelaDados].sort((a, b) => b.qtdSolicitados - a.qtdSolicitados).slice(0, 5);

      this.seriesTop5 = [{
        name: 'Chamados',
        data: top5.map(i => i.qtdSolicitados)
      }];

      this.chartOptionsTop5 = {
        ...this.chartOptionsTop5,
        xaxis: {
          categories: top5.map(i => (i.nomeColaborador || 'Sem Nome').split(' ')[0])
        }
      };

      const dadosAgrupados = this.agruparLocalizacoesPorEmpresa(this.rankingLocalizacaoDados);

      this.seriesRanking = [{
        name: 'Volume',
        data: dadosAgrupados.map(i => i.qtdSolicitados)
      }];

      this.chartOptionsRanking = {
        ...this.chartOptionsRanking,
        xaxis: {
          categories: dadosAgrupados.map(i => i.nomeLocalizacao)
        }
      };
    },

    formatarEntidade(nome) {
      if (!nome) return "-";
      const limpo = nome.replace(/^Grupo Parque\s*>\s*/i, "").trim();
      const norm = limpo.toUpperCase();
      if (norm === "GRUPO PARQUE" || norm === "ROOT ENTITY" || norm === "ENTIDADE RAIZ") {
        return "Geral";
      }
      return limpo || "-";
    },

    exportarChamadosExcel() {
      if (!this.tabelaChamados || this.tabelaChamados.length === 0) {
        alert("Não há chamados para exportar.");
        return;
      }
      this.exportandoExcel = true;
      try {
        const dadosParaExportar = this.searchChamados ? this.chamadosFiltradosPorBusca : this.tabelaChamados;

        const rows = dadosParaExportar.map(item => ({
          "ID Chamado": item.idChamado,
          "Título do Chamado": item.tituloChamado || "",
          "Solicitante": item.solicitante || "Não informado",
          "Técnico Atribuído": item.tecnicoAtribuido || "Não atribuído",
          "Status": item.status || "",
          "Localização": item.localizacao || "Sem localização",
          "Entidade / Departamento": this.formatarEntidade(item.entidade),
          "Link GLPI": `https://chamados.grupoparque.com.br/front/ticket.form.php?id=${item.idChamado}`
        }));

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Chamados GLPI");
        XLSX.writeFile(workbook, `chamados_glpi_${Date.now()}.xlsx`);
      } catch (e) {
        console.error("Erro ao exportar chamados para Excel:", e);
        alert("Erro ao exportar dados para Excel.");
      } finally {
        this.exportandoExcel = false;
      }
    }
  }
}
</script>